import type { Colors, Point, SpriteSheetItem } from './types';

type CanvasImageSource =
  | HTMLCanvasElement
  | HTMLImageElement
  | SVGImageElement
  | HTMLVideoElement
  | ImageBitmap
  | OffscreenCanvas;

const SCALE = 0.0025;
const TWO_PI = Math.PI * 2;

function rumbleWidth(projectedRoadWidth: number, div: number, lanes: number): number {
  const denom = 2 * lanes;
  return projectedRoadWidth / (div > denom ? div : denom);
}

function laneMarkerWidth(projectedRoadWidth: number, lanes: number): number {
  const denom = 8 * lanes;
  return projectedRoadWidth / (denom > 32 ? denom : 32);
}

export function polygon(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  x4: number,
  y4: number,
  color: string,
): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.lineTo(x3, y3);
  ctx.lineTo(x4, y4);
  ctx.closePath();
  ctx.fill();
}

export function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius:
    | number
    | {
        topLeft: number;
        topRight: number;
        bottomLeft: number;
        bottomRight: number;
      },
  color: string,
): void {
  let { topLeft, topRight, bottomLeft, bottomRight } = {
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0,
  };

  if (typeof radius === 'number') {
    topLeft = radius;
    topRight = radius;
    bottomLeft = radius;
    bottomRight = radius;
  }

  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x + topLeft, y);
  ctx.lineTo(x + width - topRight, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + topRight);
  ctx.lineTo(x + width, y + height - bottomRight);
  ctx.quadraticCurveTo(x + width, y + height, x + width - bottomRight, y + height);
  ctx.lineTo(x + bottomLeft, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - bottomLeft);
  ctx.lineTo(x, y + topLeft);
  ctx.quadraticCurveTo(x, y, x + topLeft, y);
  ctx.closePath();
  ctx.fill();
}

export function line(
  ctx: CanvasRenderingContext2D,
  points: Point[],
  lineWidth: number,
  color: string,
  dashPoint = 1,
): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;

  const pointsLen = points.length;
  const dashPointRatio = (pointsLen * dashPoint) | 0;

  ctx.beginPath();
  for (let i = 0; i < dashPointRatio; i++) {
    const p = points[i];
    ctx.lineTo(p.x, p.y);
  }
  ctx.stroke();

  if (dashPointRatio !== pointsLen) {
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    if (dashPointRatio > 0) {
      const lastSolid = points[dashPointRatio - 1];
      ctx.moveTo(lastSolid.x, lastSolid.y);
    }
    for (let i = dashPointRatio; i < pointsLen; i++) {
      const p = points[i];
      ctx.lineTo(p.x, p.y);
    }
    ctx.stroke();
    ctx.setLineDash([]);
  }
}

export function circle(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  color: string,
  strokeWidth?: number,
  strokeColor?: string,
  opacity = 1,
): void {
  if (opacity !== 1) ctx.globalAlpha = opacity;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TWO_PI);
  ctx.fill();
  if (strokeWidth) {
    if (strokeColor) ctx.strokeStyle = strokeColor;
    ctx.lineWidth = strokeWidth;
    ctx.stroke();
  }
  if (opacity !== 1) ctx.globalAlpha = 1;
}

export function background(
  ctx: CanvasRenderingContext2D,
  background: CanvasImageSource,
  width: number,
  height: number,
  layer: SpriteSheetItem,
  rotation = 0,
  offset = 0,
): void {
  const layerW = layer.w;
  const layerX = layer.x;
  const imageW = layerW * 0.5;
  const imageH = layer.h;

  const sourceX = layerX + ((layerW * rotation) | 0);
  const sourceY = layer.y;
  const maxSourceW = layerX + layerW - sourceX;
  const sourceW = imageW < maxSourceW ? imageW : maxSourceW;

  const destW = ((width * sourceW) / imageW) | 0;

  ctx.drawImage(background, sourceX, sourceY, sourceW, imageH, 0, offset, destW, height);

  if (sourceW < imageW) {
    ctx.drawImage(background, layerX, sourceY, imageW - sourceW, imageH, destW - 1, offset, width - destW, height);
  }
}

export function sprite(
  ctx: CanvasRenderingContext2D,
  width: number,
  roadWidth: number,
  sprites: CanvasImageSource,
  sprite: SpriteSheetItem,
  scale: number,
  destX: number,
  destY: number,
  offsetX = 0,
  offsetY = 0,
  clipY: number | undefined = undefined,
): void {
  const scaleFactor = scale * width * 0.5 * SCALE * roadWidth;
  const spriteW = sprite.w;
  const spriteH = sprite.h;
  const destW = spriteW * scaleFactor;
  const destH = spriteH * scaleFactor;

  const internalDestX = destX + destW * offsetX;
  const internalDestY = destY + destH * offsetY;

  const overflow = internalDestY + destH - (clipY ?? Infinity);
  const clipH = overflow > 0 ? overflow : 0;

  if (clipH < destH) {
    const drawH = destH - clipH;
    ctx.drawImage(
      sprites,
      sprite.x,
      sprite.y,
      spriteW,
      spriteH - (spriteH * clipH) / destH,
      internalDestX,
      internalDestY,
      destW,
      drawH,
    );
  }
}

export function road(
  ctx: CanvasRenderingContext2D,
  width: number,
  x1: number,
  y1: number,
  w1: number,
  x2: number,
  y2: number,
  w2: number,
  colors: Colors['road'],
  lanes: number,
  drawLane: boolean,
): void {
  const r1 = rumbleWidth(w1, 6, lanes);
  const r2 = rumbleWidth(w2, 6, lanes);
  const r11 = rumbleWidth(w1, 3, lanes);
  const r21 = rumbleWidth(w1, 3, lanes);

  const x1MinusW1 = x1 - w1;
  const x1PlusW1 = x1 + w1;
  const x2MinusW2 = x2 - w2;
  const x2PlusW2 = x2 + w2;

  ctx.fillStyle = colors.grass;
  ctx.fillRect(0, y2, width, y1 - y2);

  polygon(ctx, x1MinusW1 - r11, y1, x1MinusW1, y1, x2MinusW2, y2, x2MinusW2 - r21, y2, colors.rumble);
  polygon(ctx, x1MinusW1 - r1, y1, x1MinusW1, y1, x2MinusW2, y2, x2MinusW2 - r2, y2, colors.rumbleShadow);
  polygon(ctx, x1PlusW1 + r11, y1, x1PlusW1, y1, x2PlusW2, y2, x2PlusW2 + r21, y2, colors.rumble);
  polygon(ctx, x1PlusW1 + r1, y1, x1PlusW1, y1, x2PlusW2, y2, x2PlusW2 + r2, y2, colors.rumbleShadow);
  polygon(ctx, x1MinusW1, y1, x1PlusW1, y1, x2PlusW2, y2, x2MinusW2, y2, colors.background);

  if (colors.lane && drawLane) {
    const l1Half = laneMarkerWidth(w1, lanes) * 0.5;
    const l2Half = laneMarkerWidth(w2, lanes) * 0.5;
    const laneW1 = (w1 * 2) / lanes;
    const laneW2 = (w2 * 2) / lanes;

    let laneX1 = x1MinusW1 + laneW1;
    let laneX2 = x2MinusW2 + laneW2;

    for (let lane = 1; lane < lanes; lane++) {
      polygon(ctx, laneX1 - l1Half, y1, laneX1 + l1Half, y1, laneX2 + l2Half, y2, laneX2 - l2Half, y2, colors.lane);
      laneX1 += laneW1;
      laneX2 += laneW2;
    }
  }
}
